import { gql } from '@apollo/client/core';
import { executeQuery } from '@/components/commons/utils';
import { computed, ref, reactive } from 'vue';
import Tracking from '@/components/commons/Tracking';
import ColourMapping from '@/components/commons/ColourUtils.js';
import LocationService from '@/services/LocationService';
import { formatMoney } from '@/components/cart/utils';
import CountryUtils from '@/components/commons/CountryUtils';
import { Sort } from '@/components/commons/SortOptions.js';

export const SEARCH_PRODUCTS_QUERY = gql`
  query SEARCH_PRODUCTS_WITH_FILTER(
    $initialFilter: ProductFilter
    $filter: ProductFilter
    $query: String
    $sort: ProductSortType
    $page: Int
    $pageSize: Int
    $country: String
  ) {
    searchProductsWithFilter(
      initialFilter: $initialFilter
      filter: $filter
      query: $query
      sort: $sort
      page: $page
      pageSize: $pageSize
      country: $country
    ) {
      products {
        hashKey
        baseHashKey
        title
        skuNo
        slug
        imageUrl
        onSale
        onClearance
        isNewProduct
        variation {
          hashKey
          fabricAndStain
          popularity
          imageUrl
          variationImageUrl
          title
          slug
          onSale
          onClearance
          isNewProduct
          priceMap {
            USD {
              currencyCode
              price
              retail
              sale
              clearance
            }
            CAD {
              currencyCode
              price
              retail
              sale
              clearance
            }
          }
        }
        priceMap {
          USD {
            currencyCode
            price
            retail
            sale
            clearance
          }
          CAD {
            currencyCode
            price
            retail
            sale
            clearance
          }
        }
      }
      count
      maxPage
      page
      taxonomyCategories {
        id
        name
        count
        disabled
      }
      taxonomyAttributes {
        id
        name
        count
        attributes {
          id
          attributeId
          name
          valueId
          count
          disabled
        }
      }
      onSale {
        name
        count
        countInitial
      }
      onClearance {
        name
        count
        countInitial
      }
      inStock {
        name
        count
        countInitial
      }
      queryAfterRemoval
      queryID
    }
  }
`;

function createDefaultFilter() {
  return {
    marketingTags: [],
    taxonomyCategories: [],
    taxonomyAttributes: [],
    collectionIds: [],
    onSale: false,
    onClearance: false,
    inStock: false,
    excludedProducts: [],
    query: '',
  };
}

export default function useProductSearch() {
  const searchProductsRef = ref({ products: [] });
  const productPages = ref([]);
  const pending = ref(false);

  const initialFilter = reactive({
    ...createDefaultFilter(),
  });

  const filter = reactive({
    ...createDefaultFilter(),
  });

  const defaultSort = ref(Sort.Popularity.id);
  const sort = ref(Sort.Popularity.id);
  const pageSize = ref(24);

  function updateInitialFilter(f) {
    Object.assign(initialFilter, f || {});
    Object.assign(filter, createDefaultFilter(), {
      onSale: initialFilter.onSale,
      onClearance: initialFilter.onClearance,
      inStock: initialFilter.inStock,
    });
  }

  function updateFilter(f) {
    Object.assign(filter, f || {});
  }

  function updateDefaultSort(updatedSort) {
    defaultSort.value = updatedSort;
    sort.value = updatedSort;
  }

  function updateSort(updatedSort) {
    sort.value = updatedSort;
  }

  function updatePageSize(size) {
    pageSize.value = size;
  }

  function clearFilter() {
    Object.assign(filter, createDefaultFilter(), {
      onSale: initialFilter.onSale,
      onClearance: initialFilter.onClearance,
      inStock: initialFilter.inStock,
    });
    sort.value = defaultSort.value;
  }

  function formatProduct(product) {
    const currencyCode = LocationService.isCanada() ? 'CAD' : 'USD';

    product.imageUrl = `${product.imageUrl}?fit=max&w={width}&q={q}`;
    product.variationImageUrl = `${product.variationImageUrl}?fit=max&w={width}&q={q}`;

    let priceKey = 'price';
    if (product.onClearance) {
      priceKey = 'clearance';
    } else if (product.onSale) {
      priceKey = 'sale';
    }

    let { priceMap } = product;
    if (priceMap) {
      priceMap = priceMap[currencyCode];
      product.originalPrice = formatMoney(CountryUtils.getCountryByCurrencyId(LocationService.getCurrencyId()).currencySymbol, priceMap.price);
      product.priceCents = priceMap[priceKey] || priceMap.price;
      product.price = formatMoney(CountryUtils.getCountryByCurrencyId(LocationService.getCurrencyId()).currencySymbol, product.priceCents);
    }

    if (product.variation) {
      product.variation = product.variation.map(formatProduct);
    }

    return product;
  }

  const maxPage = computed(() => searchProductsRef.value.maxPage || 0);

  const taxonomyCategories = computed(() => searchProductsRef.value.taxonomyCategories || []);

  const taxonomyAttributes = computed(() => {
    const result = searchProductsRef?.value?.taxonomyAttributes ? JSON.parse(JSON.stringify(searchProductsRef.value.taxonomyAttributes)) : [];

    const colorAttribute = result?.find((attribute) => attribute.name === 'Color');
    if (colorAttribute) {
      colorAttribute.attributes.forEach((value) => {
        value.hex = ColourMapping[value.name];
      });
    }

    return result || [];
  });

  const products = computed(() => {
    return productPages.value.flat();
  });

  const productsCount = computed(() => searchProductsRef.value.count);

  const loadedPageCount = computed(() => productPages.value.length);

  const isNoProducts = computed(() => !products.value?.length);

  const inStockCountInitial = computed(() => searchProductsRef.value.inStock?.find((stock) => stock.name === 'true')?.countInitial ?? 0);
  const onSaleCountInitial = computed(() => searchProductsRef.value.onSale?.find((sale) => sale.name === 'true')?.countInitial ?? 0);
  const onClearanceCountInitial = computed(
    () => searchProductsRef.value.onClearance?.find((clearance) => clearance.name === 'true')?.countInitial ?? 0,
  );

  const inStockCount = computed(() => searchProductsRef.value.inStock?.find((stock) => stock.name === 'true')?.count ?? 0);
  const onSaleCount = computed(() => searchProductsRef.value.onSale?.find((sale) => sale.name === 'true')?.count ?? 0);
  const onClearanceCount = computed(() => searchProductsRef.value.onClearance?.find((clearance) => clearance.name === 'true')?.count ?? 0);

  const queryAfterRemoval = computed(() => searchProductsRef.value.queryAfterRemoval || '');

  const queryID = computed(() => searchProductsRef.value.queryID || '');

  const filterCount = computed(() => {
    return (
      filter.taxonomyCategories.length +
      filter.taxonomyAttributes.length +
      Number(initialFilter.onClearance !== filter.onClearance) +
      Number(initialFilter.onSale !== filter.onSale) +
      Number(initialFilter.inStock !== filter.inStock) +
      Number(defaultSort.value !== sort.value)
    );
  });

  async function load(page = 0, replace = true) {
    pending.value = true;
    try {
      const params = {
        initialFilter: initialFilter,
        filter: filter,
        sort: sort.value,
        page: page,
        pageSize: pageSize.value,
        country: CountryUtils.getCountryByCurrencyId(LocationService.getCurrencyId()).code,
      };

      let { searchProductsWithFilter } = await executeQuery(SEARCH_PRODUCTS_QUERY, params);
      searchProductsWithFilter = JSON.parse(JSON.stringify(searchProductsWithFilter));
      searchProductsWithFilter.products = (searchProductsWithFilter.products || []).map(formatProduct);

      if (replace) {
        searchProductsRef.value = searchProductsWithFilter;
        productPages.value = [];
      }
      productPages.value[page] = searchProductsWithFilter.products;
    } catch (error) {
      console.error(error);
      Tracking.sentryCaptureError(error);
    }
    pending.value = false;
  }

  async function loadMore() {
    await load(loadedPageCount.value, false);
  }

  return {
    pending,
    initialFilter,
    filter,
    sort,
    pageSize,
    updateInitialFilter,
    updateFilter,
    updatePageSize,
    updateDefaultSort,
    updateSort,
    clearFilter,
    products,
    maxPage,
    taxonomyCategories,
    taxonomyAttributes,
    isNoProducts,
    productsCount,
    loadedPageCount,

    inStockCountInitial,
    onSaleCountInitial,
    onClearanceCountInitial,
    inStockCount,
    onSaleCount,
    onClearanceCount,

    queryAfterRemoval,
    queryID,
    filterCount,
    load,
    loadMore,
  };
}
